/*
 * <heritage-header-search>
 *
 * Toggles a drop-down search panel anchored to the header search
 * icon. The panel hosts a native <form action="/search" method="get">
 * that submits to the heritage search-results template.
 *
 * Behavior:
 *   • Icon click            → toggle panel open/closed
 *   • Panel open            → focus the input
 *   • Escape key            → close
 *   • Click outside         → close
 *   • Close button (×)      → close
 *   • Submit                → native form submission (no interception)
 */

class HeritageHeaderSearch extends HTMLElement {
  constructor() {
    super();
    this.handleToggle = this.handleToggle.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleDocClick = this.handleDocClick.bind(this);
  }

  connectedCallback() {
    this.toggleBtn = this.querySelector('[data-search-toggle]');
    this.panel = this.querySelector('[data-search-panel]');
    this.input = this.querySelector('[data-search-input]');
    this.closeBtns = this.querySelectorAll('[data-search-close]');

    if (this.toggleBtn) this.toggleBtn.addEventListener('click', this.handleToggle);
    this.closeBtns.forEach((b) => b.addEventListener('click', this.handleClose));
    document.addEventListener('keydown', this.handleKeydown);
    document.addEventListener('click', this.handleDocClick);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.handleKeydown);
    document.removeEventListener('click', this.handleDocClick);
  }

  isOpen() {
    return this.toggleBtn && this.toggleBtn.getAttribute('aria-expanded') === 'true';
  }

  open() {
    if (this.panel) this.panel.hidden = false;
    if (this.toggleBtn) this.toggleBtn.setAttribute('aria-expanded', 'true');
    // Lock body scroll while the full-screen mobile overlay is open. The CSS
    // only applies overflow:hidden at the mobile breakpoint, so this is a
    // no-op for the desktop drop-down.
    document.body.classList.add('heritage-search-open');
    // Defer focus so the panel finishes hiding-toggle paint first.
    window.setTimeout(() => {
      if (this.input) this.input.focus();
    }, 30);
  }

  close() {
    const wasOpen = this.isOpen();
    if (this.panel) this.panel.hidden = true;
    if (this.toggleBtn) this.toggleBtn.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('heritage-search-open');
    // Return focus to the trigger so keyboard/AT users aren't dropped at the
    // top of the document after the overlay closes.
    if (wasOpen && this.toggleBtn) this.toggleBtn.focus();
  }

  handleToggle(event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.isOpen()) this.close();
    else this.open();
  }

  handleClose(event) {
    if (event) event.preventDefault();
    this.close();
  }

  handleKeydown(event) {
    if (event.key === 'Escape' && this.isOpen()) this.close();
  }

  handleDocClick(event) {
    if (!this.isOpen()) return;
    if (this.contains(event.target)) return;
    this.close();
  }
}

if (!customElements.get('heritage-header-search')) {
  customElements.define('heritage-header-search', HeritageHeaderSearch);
}
